<template>
  <div ref="rootElement" class="mb-[5rem]">
    <!-- 상단 헤더 & 버튼 -->
    <div class="flex items-center justify-end mb-[4rem]">
      <button
        @click="handleReset"
        class="btn-secondary w-1/6 font-semibold text-green-400"
      >
        <i class="bi bi-arrow-repeat mr-2"></i>
        다시 분석하기
      </button>
    </div>

    <!-- 탭 메뉴 -->
    <div class="flex gap-4 mb-6 border-b border-dark-600">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        @click="selectedTab = tab.key"
        :class="[
          'px-6 py-3 text-base font-semibold transition-colors border-b-2',
          selectedTab === tab.key
            ? 'text-white border-primary'
            : 'text-gray-400 border-transparent hover:text-gray-300'
        ]"
      >
        {{ tab.label }}
        <span :class="[
          'ml-2 px-2 py-0.5 rounded-full text-xs',
          selectedTab === tab.key ? tab.badgeClass : 'bg-dark-700 text-gray-500'
        ]">
          {{ getTabCount(tab.key) }}
        </span>
      </button>
    </div>

    <!-- 검증 결과 목록 -->
    <div class="space-y-8">
      <div v-for="(result, index) in filteredResults" :key="index" class="card overflow-hidden">
      <!-- 최상단 헤더: 검증 대상 문장 -->
      <div class="flex items-start justify-between gap-4 pb-3 mb-6 border-b-2 border-dark-600">
        <!-- 문장 -->
        <div class="flex items-start gap-2 flex-1 min-w-0">
          <div class="flex-1">
            <span class="text-white text-xl font-semibold leading-relaxed">{{ result.verification_sentence }}</span>
            <span class="text-caption text-white-500 ml-2">검증 대상 문장</span>
          </div>
        </div>
        
        <!-- 배지 -->
        <div class="flex-shrink-0">
          <div :class="getResultClass(result.overall_result)" class="badge px-4 py-2 text-sm">
            {{ result.overall_result }}
          </div>
        </div>
      </div>

      <!-- 헤더 정보 -->
      <div class="bg-dark-600 px-8 py-6 border-b border-dark-600">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-list-ul mr-2 text-blue-400"></i>목차
            </p>
            <p class="text-white text-base">{{ result.section_title }}</p>
          </div>
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-hash mr-2 text-blue-400"></i>문장 ID
            </p>
            <p class="text-blue-400 font-bold text-lg">{{ result.id }}</p>
          </div>
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-check-circle mr-2 text-green-400"></i>검증 유형
            </p>
            <p class="text-white text-base">{{ result.verification_type }}</p>
          </div>
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-exclamation-triangle mr-2 text-red-400"></i>위험도 등급
            </p>
            <p class="text-red-400 font-bold text-lg">{{ result.risk_level }}</p>
          </div>
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-chat-left-text mr-2 text-purple-400"></i>검증 필요 사유
            </p>
            <p class="text-gray-300 text-base leading-relaxed">{{ result.verification_need_reason }}</p>
          </div>
          <div>
            <p class="text-caption mb-2">
              <i class="bi bi-file-earmark-text mr-2 text-yellow-400"></i>페이지 위치
            </p>
            <p class="text-white font-bold text-lg">{{ result.page_number }}</p>
          </div>
        </div>
      </div>

      <!-- 상세 검증 -->
      <div v-if="result.detailed_sentence_verifications" class="space-y-6 my-7">
        <div v-for="(detail, detailIndex) in result.detailed_sentence_verifications" :key="detailIndex" class="p-6 bg-dark-800 rounded border border-dark-600">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-8 h-8 rounded-full bg-primary bg-opacity-30 flex items-center justify-center">
              <span class="text-primary font-bold text-sm">{{ detailIndex + 1 }}</span>
            </div>

            <div class="flex-1">
              <!-- 검증 문장 & 배지 -->
              <div class="flex items-start justify-between gap-4 mb-8">
                <div class="flex-1 min-w-0">
                  <p class="text-caption mb-2">개별 검증 문장</p>
                  <p class="text-white text-base leading-relaxed font-semibold">
                    {{ detail.detailed_sentence }}
                  </p>
                </div>
                <div class="flex-shrink-0">
                  <span :class="getDetailResultClass(detail.detailed_sentence_result)" class="badge px-4 py-2">
                    {{ detail.detailed_sentence_result }}
                  </span>
                </div>
              </div>

              <!-- 검증 내용 -->
              <div v-if="detail.detailed_sentence_source_content" class="mb-8">
                <p class="text-caption mb-2">검증 내용</p>
                <p class="text-white text-base leading-relaxed">
                  {{ detail.detailed_sentence_source_content }}
                </p>
              </div>

              <!-- 검증 사유 -->
              <div class="mb-8">
                <p class="text-caption mb-2">{{ detail.detailed_sentence_result }} 사유</p>
                <p class="text-white text-base leading-relaxed">
                  {{ detail.detailed_sentence_reason }}
                </p>
              </div>

              <!-- 참고 자료 -->
              <div v-if="detail.detailed_sentence_source_title && detail.detailed_sentence_source_title.length > 0">
                <p class="text-caption mb-3">참고 자료 ({{ detail.detailed_sentence_source_title.length }}개)</p>
                <div class="space-y-3">
                  <div v-for="(source, sourceIndex) in detail.detailed_sentence_source_title" :key="sourceIndex" class="bg-dark-700 rounded-lg p-4 border border-dark-600">
                    <h6 class="text-white font-semibold text-sm mb-3">{{ source }}</h6>
                    <div class="flex items-center justify-between gap-4">
                      <a
                        v-if="detail.detailed_sentence_source_url?.[sourceIndex]"
                        :href="detail.detailed_sentence_source_url[sourceIndex]"
                        target="_blank"
                        class="text-gray-400 hover:text-gray-300 underline text-xs break-all inline-flex items-center gap-1 flex-1"
                      >
                        <i class="bi bi-box-arrow-up-right text-xs"></i>
                        {{ detail.detailed_sentence_source_url[sourceIndex] }}
                      </a>
                      <span v-else class="text-gray-500 text-xs flex-1">URL 정보 없음</span>
                      <span class="text-gray-400 text-xs whitespace-nowrap">
                        {{ detail.detailed_sentence_source_date?.[sourceIndex] || '-' }}
                      </span>
                    </div>
                   </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>

      <!-- 종합 평가 -->
      <div class="mb-7">
        <p class="text-white font-bold text-base mb-4">
          <i class="bi bi-check-circle-fill mr-2 text-blue-500"></i>종합 평가
        </p>
        <p class="text-gray-200 text-base bg-dark-700 rounded-lg p-6 leading-relaxed border-l-4 border-blue-500">
          {{ result.overall_assessment }}
        </p>
      </div>

      <!-- 주의사항 -->
      <div v-if="result.caution">
        <p class="text-white font-bold text-base mb-4">
          <i class="bi bi-exclamation-triangle-fill mr-2 text-yellow-400"></i>주의사항
        </p>
        <div class="bg-yellow-900 bg-opacity-20 border-l-4 border-yellow-500 rounded-lg p-6">
          <p class="text-gray-200 text-base leading-relaxed">
            {{ result.caution }}
          </p>
        </div>
        </div>
      </div>
      </div>
    </div>

    <!-- 액션 버튼 -->
    <div class="flex gap-10 mb-[5rem]">
      <button
        @click="scrollToTop"
        class="flex-1 btn-secondary"
      >
        <i class="bi bi-arrow-up mr-2"></i>맨 위로 올라가기
      </button>
      <button
        @click="handleReset"
        class="flex-1 btn-primary"
      >
        <i class="bi bi-arrow-repeat mr-2"></i>다시 분석하기
      </button>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useVerificationStore } from '@/stores/verification';

const store = useVerificationStore();
const selectedTab = ref('all');
const rootElement = ref(null);

const tabs = [
  { key: 'all', label: '전체', badgeClass: 'bg-dark-700 text-gray-400' },
  { key: '검증가능', label: '검증가능', badgeClass: 'bg-green-500/20 text-green-400' },
  { key: '자료기반추정', label: '자료기반추정', badgeClass: 'bg-blue-500/20 text-blue-400' },
  { key: '거짓', label: '거짓', badgeClass: 'bg-red-500/20 text-red-400' },
  { key: '검증불가', label: '검증불가', badgeClass: 'bg-yellow-500/20 text-yellow-400' },
];

const filteredResults = computed(() => {
  if (!store.verificationResults) return [];
  
  if (selectedTab.value === 'all') {
    return store.verificationResults;
  }
  
  return store.verificationResults.filter(result => {
    return result.overall_result === selectedTab.value;
  });
});

const getTabCount = (tabKey) => {
  if (!store.verificationResults) return 0;
  
  if (tabKey === 'all') {
    return store.verificationResults.length;
  }
  
  return store.verificationResults.filter(result => {
    return result.overall_result === tabKey;
  }).length;
};

const getResultClass = (result) => {
  const classes = {
    '검증가능': 'bg-green-900 text-green-200',
    '검증불가': 'bg-yellow-900 text-yellow-200',
    '거짓': 'bg-red-900 text-red-200',
    '자료기반추정': 'bg-blue-900 text-blue-200',
  };
  return classes[result] || 'bg-gray-900 text-gray-200';
};

const getDetailResultClass = (result) => {
  const classes = {
    '검증가능': 'bg-green-900 text-green-200',
    '검증불가': 'bg-yellow-900 text-yellow-200',
    '거짓': 'bg-red-900 text-red-200',
    '자료기반추정': 'bg-blue-900 text-blue-200',
  };
  return classes[result] || 'bg-gray-900 text-gray-200';
};

const handleReset = () => {
  store.reset();
};

const scrollToTop = () => {
  // rootElement를 통해 부모 요소 중 overflow-auto를 가진 요소 찾기
  if (rootElement.value) {
    let parent = rootElement.value.parentElement;
    while (parent && parent !== document.body) {
      // overflow-auto 클래스를 가진 요소 찾기
      if (parent.classList && parent.classList.contains('overflow-auto')) {
        parent.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // className 문자열에서도 확인
      const className = parent.className || '';
      if (typeof className === 'string' && className.includes('overflow-auto')) {
        if (parent.scrollTo) {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          parent.scrollTop = 0;
        }
        return;
      }
      parent = parent.parentElement;
    }
  }
  
  // fallback: 직접 overflow-auto 요소 찾기
  const overflowElements = document.querySelectorAll('.overflow-auto');
  if (overflowElements.length > 0) {
    const container = overflowElements[0];
    if (container.scrollTo) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      container.scrollTop = 0;
    }
    return;
  }
  
  // fallback: main 요소와 window 스크롤
  const mainElement = document.querySelector('main');
  if (mainElement && mainElement.scrollTo) {
    mainElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

